import {
  AccessUsersParams,
  IAnalysisUser,
  UserAccessFilters,
  UserAccessObjects,
  UserAccessUsers,
  FetchReviewAnalysisParams,
  UserAccessPrincipalTypes,
  ReviewAnalysisParams,
  IAnalysisGroup,
  IAnalysis,
  AccessAuditLogsParams,
  AnalysisAuditLog,
  AccessReviewChangeReviewStatusParams,
  AccessReviewObjectsParams,
  AccessReviewObject,
  AnalysisEditParams,
  AccessAuditLogObjectsParams,
  AccessReviewStatuses,
  AccessReviewRevokeParams
} from './accessReviewSlice'
import { GRAPHQL_API_FILTERS, LIMIT_DEFAULT } from '../../../constants'
import { getAfterCursor, parameterizeArrayofObjects } from '../../../utils/graphqlUtil'
import { gql } from 'graphql-request'

export const queryAnalysisList = (params: FetchReviewAnalysisParams): string => {
  const { page = 1, pageSize, dataSourceIds, analysisStatus, createdBy, searchText = '' } = params

  let filterString = ''

  if (dataSourceIds) {
    filterString += `{ key: ${GRAPHQL_API_FILTERS.datasourceId}, values: ${JSON.stringify(
      dataSourceIds
    )}}, `
  }
  if (analysisStatus) {
    filterString += `{ key: ${GRAPHQL_API_FILTERS.status}, values: ${JSON.stringify(
      analysisStatus
    )}}, `
  }
  if (createdBy) {
    filterString += `{ key: ${GRAPHQL_API_FILTERS.createdBy}, values: ["${createdBy}"] }, `
  }

  return gql`
    {
      accessReviews(
        first: ${pageSize},
        after: "${getAfterCursor(page, pageSize)}",
        searchText: "${searchText}",
        ${filterString ? `filter: [${filterString}]` : ''}
      ) {
        count
        edges {
          node {
            id
            title
            path
            workflowStatus
            status
            createdAt
            createdBy
            ownerEmail
            updateTimestamp
            datasource {
              edges {
                node {
                  id
                  name
                  type
                }
              }
            }
          }
        }
      }
    }
  `
}
export const mapQueryAnalysisList = (raw: any): { list: IAnalysis[]; count: number } => {
  const analysisList: IAnalysis[] = raw.accessReviews.edges.map(({ node }) => ({
    id: node.id,
    name: node.title,
    path: node.path,
    workflowStatus: node.workflowStatus,
    reviewStatus: node.status,
    createdAt: node.createdAt,
    createdBy: node.createdBy,
    ownerEmail: node.ownerEmail,
    updateTimestamp: node.updateTimestamp,
    dataSource:
      node.datasource && node.datasource.edges[0]
        ? {
            id: node.datasource.edges[0].node.id,
            name: node.datasource.edges[0].node.name,
            type: node.datasource.edges[0].node.type
          }
        : null
  }))

  return { list: analysisList, count: raw.accessReviews.count }
}

export const queryTotalAnalysis = (): string => {
  return gql`
    {
      accessReviews {
        count
      }
    }
  `
}
export const mapQueryTotalAnalysis = (raw: any): number => {
  return raw.accessReviews.count
}

export const queryAnalysisDetailsById = (reviewId: string) => {
  return gql`
    {
      accessReviews(reviewId: "${reviewId}") {
        edges {
          node {
            title
            path
            workflowStatus
            status
            createdAt
            createdBy
            ownerEmail
            updateTimestamp
            datasource {
              edges {
                node {
                  id
                  name
                  type
                  status: state
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapQueryAnalysisDetailsById = (raw: any): IAnalysis => {
  const accessReview = raw.accessReviews.edges[0].node
  return {
    name: accessReview?.title,
    path: accessReview?.path,
    webUrl: accessReview?.webUrl,
    workflowStatus: accessReview?.workflowStatus,
    reviewStatus: accessReview?.status,
    createdAt: accessReview?.createdAt,
    updateTimestamp: accessReview?.updateTimestamp,
    createdBy: accessReview?.createdBy,
    ownerEmail: accessReview?.ownerEmail,
    dataSource: accessReview?.datasource?.edges[0]?.node
  }
}

export const queryAnalysisOverviewDetailsById = (reviewId: string) => {
  return gql`
    {
      allUsers: accessReviewPrincipals(reviewId: "${reviewId}", principalType: USER) {
        count
      }
      externalUsers: accessReviewPrincipals(
        reviewId: "${reviewId}"
        principalType: USER
        booleanFilter: [{ key: IS_EXTERNAL_USER, value: true }]
      ) {
        count
      }
      directAccessUsers: accessReviewPrincipals(
        reviewId: "${reviewId}"
        principalType: USER
        booleanFilter: [{ key: HAS_DIRECT_ACCESS, value: true }]
      ) {
        count
      }
      groupAccessUsers: accessReviewPrincipals(
        reviewId: "${reviewId}"
        principalType: USER
        booleanFilter: [{ key: HAS_GROUP_ACCESS, value: true }]
      ) {
        count
      }
      unReviewedUsers: accessReviewPrincipals(
        reviewId: "${reviewId}"
        principalType: USER
        filter: [{ key: ${GRAPHQL_API_FILTERS.principalStatus}, values: ["${AccessReviewStatuses.notReviewed}"] }]
      ) {
        count
      }
      reviewedUsers: accessReviewPrincipals(
        reviewId: "${reviewId}"
        principalType: USER
        filter: [{ key: ${GRAPHQL_API_FILTERS.principalStatus}, values: ["${AccessReviewStatuses.reviewed}"] }]
      ) {
        count
      }
      flaggedUsers: accessReviewPrincipals(
        reviewId: "${reviewId}"
        principalType: USER
        filter: [{ key: ${GRAPHQL_API_FILTERS.principalStatus}, values: ["${AccessReviewStatuses.flagged}"] }]
      ) {
        count
      }
       unReviewedGroups: accessReviewPrincipals(
        reviewId: "${reviewId}"
        principalType: GROUP
        filter: [{ key: ${GRAPHQL_API_FILTERS.principalStatus}, values: ["${AccessReviewStatuses.notReviewed}"] }]
      ) {
        count
      }
      flaggedGroups: accessReviewPrincipals(
        reviewId: "${reviewId}"
        principalType: GROUP
        filter: [{ key: ${GRAPHQL_API_FILTERS.principalStatus}, values: ["${AccessReviewStatuses.flagged}"] }]
      ) {
        count
      }
    }
  `
}

export const mapQueryAnalysisOverviewDetailsById = (raw: any): IAnalysis['overview'] => {
  const {
    allUsers,
    externalUsers,
    directAccessUsers,
    groupAccessUsers,
    unReviewedUsers,
    reviewedUsers,
    flaggedUsers,
    unReviewedGroups,
    flaggedGroups
  } = raw
  return {
    allUsers: allUsers.count || 0,
    externalUsers: externalUsers.count || 0,
    internalUsers: +allUsers.count - +externalUsers.count,
    directAccessUsers: directAccessUsers.count || 0,
    groupAccessUsers: groupAccessUsers.count || 0,
    unReviewedUsers: unReviewedUsers.count || 0,
    reviewedUsers: reviewedUsers.count || 0,
    flaggedUsers: flaggedUsers.count || 0,
    unReviewedGroups: unReviewedGroups.count || 0,
    flaggedGroups: flaggedGroups.count || 0
  }
}

export const queryAccessObjectCounts = (reviewId: string): string => {
  return gql`
    {
      accessReviewCounts(reviewId: "${reviewId}") {
        edges {
          node {
            totalFilesCount
            openAccessFilesCount
          }
        }
      }
    }
  `
}
export const mapQueryAccessObjectCounts = (raw: any): UserAccessObjects => {
  return {
    totalFilesCount: raw.accessReviewCounts?.edges[0]?.node?.totalFilesCount || 0,
    openAccessFilesCount: raw.accessReviewCounts?.edges[0]?.node?.openAccessFilesCount || 0
  }
}

export const queryCreateNewReviewAnalysis = (params: ReviewAnalysisParams) => {
  const {
    datasource,
    folderPath,
    analysisName,
    directoryId,
    analysisOwner,
    rootId,
    flowId = ''
  } = params

  const fragmentFlowId = flowId ? `flowId: "${flowId}"` : ''

  return `
    mutation {
      createAccessReview(actionInput: {
          clientMutationId: "1"
          datasourceId: "${datasource}"
          reportName: ${JSON.stringify(analysisName)},
          directoryId: "${directoryId}"
          rootId: "${rootId}"
          path: ${JSON.stringify(folderPath)}
          ownerEmail: "${analysisOwner}"
          ${fragmentFlowId}
        }) {
        clientMutationId
      }
    }
  `
}

export const mutationReRunAnalysis = (reviewId: string) => {
  return `
    mutation {
      syncAccessReview(
        clientMutationId: "mutation_id"
        accessReviewId: "${reviewId}"
      ) {
        clientMutationId
      }
    }
  `
}
export const queryMarkReviewAsCompleted = (reviewId: string) => {
  return `
    mutation{
      completeAccessReview(
        clientMutationId: "completeAccessReview-${reviewId}",
        accessReviewId: "${reviewId}"
      ) {
        clientMutationId
      }
    }
  `
}
export const mutationEditAnalysis = (params: AnalysisEditParams) => {
  const { reviewId, name, ownerEmail } = params
  return `
    mutation {
      updateAccessReview(
        clientMutationId: "mut_id"
        actionInput: {
          reviewId: "${reviewId}"
          reportName: "${name}"
          ownerEmail: "${ownerEmail}"
        }
      ) {
        clientMutationId
      }
    }
  `
}
export const queryDeleteReviewAnalysis = (reviewId: string) => {
  return `
    mutation{
      closeAccessControlReview(
        clientMutationId: "abs",
        accessReviewId: "${reviewId}"
      ) {
        clientMutationId
        reviewId
      }
    }
  `
}

export const queryAccessUserCounts = (reviewId: string): string => {
  return gql`
  {
    totalUsers: accessReviewPrincipals(reviewId: "${reviewId}", principalType: ${UserAccessPrincipalTypes.user}) {
      count
    }
    externalUsers: accessReviewPrincipals(
      reviewId: "${reviewId}",
      principalType: ${UserAccessPrincipalTypes.user},
      booleanFilter: [{ key: ${GRAPHQL_API_FILTERS.isExternalUser}, value: true }]
    ){
      count
    }
  }
  `
}
export const mapQueryAccessUserCounts = (raw: any): UserAccessUsers => {
  return {
    totalUsersCount: raw.totalUsers?.count || 0,
    externalUsersCount: raw.externalUsers?.count || 0
  }
}

export type AccessFiltersParams = {
  page: number
}

export const queryAccessFilters = (params: AccessFiltersParams): string => {
  const { page = 1 } = params
  const cursor = getAfterCursor(page, LIMIT_DEFAULT)
  return gql`
    {
      accessControlUsersGroupedByDept(first: ${LIMIT_DEFAULT}, after: "${cursor}") {
        count
        edges {
          node {
            departmentName
          }
        }
      }
    }
  `
}
export const mapQueryAccessFilters = (raw: any): UserAccessFilters => {
  return {
    departments:
      raw.accessControlUsersGroupedByDept?.edges?.map(({ node }) => node.departmentName) || [],
    totalDepartments: raw.accessControlUsersGroupedByDept?.count || 0
  }
}

export const queryAccessUsers = (params: AccessUsersParams): string => {
  const { page = 1, reviewId, principalType, search = '', filters } = params

  const cursor = getAfterCursor(page, LIMIT_DEFAULT)

  let filter = ''
  if (filters?.filter?.length) {
    const filterString = parameterizeArrayofObjects(filters.filter)
    filter += `filter: ${filterString}, `
  }

  let booleanFilter = ''
  if (filters?.booleanFilter?.length) {
    booleanFilter = `booleanFilter: ${parameterizeArrayofObjects(filters.booleanFilter)}, `
  }

  return gql`
    query {
      accessReviewPrincipals(
        first: ${LIMIT_DEFAULT},
        after: "${cursor}",
        reviewId: "${reviewId}",
        principalType: ${principalType},
        ${filter}
        ${booleanFilter}
        search: "${search}"
      ) {
        count
        edges {
          node {
            id
            name
            email
            sensitiveFileCount
            totalFileCount
            totalFolderCount
            department
            employeeType
            reviewStatus
            reviewedBy
            reviewedOn
            userType
            accessViaGroups {
              edges {
                node {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  `
}
export const mapQueryAccessUsers = (raw: any): { list: IAnalysisUser[]; total: number } => {
  const list: IAnalysisUser[] =
    raw.accessReviewPrincipals?.edges?.map(({ node }) => ({
      id: node.id || '',
      name: node.name || '',
      sensitiveObjectsCount: node.sensitiveFileCount || 0,
      totalObjectsCount: node.totalFileCount || 0,
      totalFoldersCount: node.totalFolderCount || 0,
      department: node.department || '',
      groupsCount: node.accessViaGroups?.edges?.length || 0,
      groupsList: node.accessViaGroups?.edges?.map(({ node }) => node) || [],
      employmentType: node.employeeType || '',
      reviewStatus: node.reviewStatus || '',
      reviewedBy: node.reviewedBy || '',
      reviewDate: node.reviewedOn || '',
      userType: node.userType || '',
      accessTypes: node.accessTypes || []
    })) || []

  return { list, total: raw.accessReviewPrincipals?.count || 0 }
}

export const queryAccessGroups = (params: AccessUsersParams): string => {
  const { page = 1, reviewId, principalType, search = '', filters } = params

  const cursor = getAfterCursor(page, LIMIT_DEFAULT)

  let filter = ''
  if (filters?.filter?.length) {
    const filterString = parameterizeArrayofObjects(filters.filter)
    filter += `filter: ${filterString}, `
  }

  let booleanFilter = ''
  if (filters?.booleanFilter?.length) {
    booleanFilter = `booleanFilter: ${parameterizeArrayofObjects(filters.booleanFilter)}, `
  }

  return gql`
    query {
      accessReviewPrincipals(
        first: ${LIMIT_DEFAULT},
        after: "${cursor}",
        reviewId: "${reviewId}",
        principalType: ${principalType},
        ${filter}
        ${booleanFilter}
        search: "${search}"
      ) {
        count
        edges {
          node {
            id
            name
            totalFileCount
            reviewStatus
            sensitiveFileCount
            reviewedBy
            reviewedOn
            memberUsers {
              count
            }
          }
        }
      }
    }
  `
}

export const mapQueryAccessGroups = (raw: any): { list: IAnalysisGroup[]; total: number } => {
  const list: IAnalysisGroup[] =
    raw.accessReviewPrincipals?.edges?.map(({ node }) => ({
      id: node.id,
      name: node.name,
      objectsCount: node.totalFileCount,
      employmentType: node.employeeType,
      reviewStatus: node.reviewStatus,
      reviewDate: node.reviewedOn,
      membersCount: node.memberUsers.count,
      sensitiveObjectsCount: node.sensitiveFileCount,
      totalObjectsCount: node.totalFileCount
    })) || []

  return { list, total: raw.accessReviewPrincipals?.count || 0 }
}

export const mutationChangeReviewStatus = (
  params: AccessReviewChangeReviewStatusParams
): string => {
  const { reviewId, principalIds, reviewStatus } = params

  return gql`
    mutation {
      updateAccessReviewAuditState(
        actionInput: {
          clientMutationId: "1"
          reviewId: "${reviewId}"
          principalIds: [${principalIds}]
          reviewStatus: ${reviewStatus}
        }
      ) {
        clientMutationId
      }
    }
  `
}

export const mutationRevokeAccess = (params: AccessReviewRevokeParams): string => {
  const { reviewId, principalIds } = params

  return gql`
    mutation {
      revokePrincipalAccess(
        clientMutationId: "revokeAccess"
        reviewId: "${reviewId}",
        principalIds: [${principalIds.map((id) => `"${id}"`).join(', ')}]
      ){
        clientMutationId
        reviewId
        principalIds
      }
    }
  `
}

export const queryAccessAuditLogs = (params: AccessAuditLogsParams): string => {
  const { reviewId, page, filters } = params

  const cursor = getAfterCursor(page, LIMIT_DEFAULT)

  let filter = ''
  if (filters?.filter?.length) {
    const filterString = parameterizeArrayofObjects(filters.filter)
    filter += `filter: ${filterString}, `
  }

  return gql`
    {
      auditHistory(first: ${LIMIT_DEFAULT}, after: "${cursor}", ${filter} entityType: ACCESS_REVIEW, entityId: "${reviewId}") {
        count
        edges {
          node {
            entityId
            entityType
            triggeredBy
            triggeredByType
            triggeredTimestamp
            completionTimestamp
            status
            workflowId
            createdAt
            updatedAt
            updateTimestamp
            stateChangeFields
            actionMetadata {
              action
              data {
                id
                type
              }
            }
          }
        }
      }
    }
  `
}
export const mapQueryAccessAuditLogs = (raw: any): { list: AnalysisAuditLog[]; total: number } => {
  try {
    const list: AnalysisAuditLog[] =
      raw.auditHistory.edges.map(({ node }) => ({
        triggeredTimestamp: node.triggeredTimestamp,
        entities: node.actionMetadata?.data,
        action: node.actionMetadata?.action,
        status: node.status,
        triggeredBy: node.triggeredBy,
        triggeredByType: node.triggeredByType
      })) || []

    return { list, total: raw.auditHistory?.count || 0 }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryAccessAuditLogPrincipals = (params: AccessAuditLogObjectsParams): string => {
  const { reviewId, type, filters } = params

  let filter = ''
  if (filters?.filter?.length) {
    const filterString = parameterizeArrayofObjects(filters.filter)
    filter += `filter: ${filterString}, `
  }

  return gql`
    query {
      accessReviewPrincipals(
        first: ${LIMIT_DEFAULT},
        reviewId: "${reviewId}",
        principalType: ${type},
        ${filter}
      ) {
        count
        edges {
          node {
            id
            name
            email
          }
        }
      }
    }
  `
}
export const mapQueryAccessAuditLogPrincipals = (raw: any) => {
  try {
    return (
      raw.accessReviewPrincipals.edges.map(({ node }) => ({
        id: node?.id,
        name: node?.name,
        email: node?.email
      })) || []
    )
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryAccessReviewObjectsList = (params: AccessReviewObjectsParams): string => {
  const { reviewId, page, pageSize = LIMIT_DEFAULT, hasOpenAccess } = params

  const cursor = getAfterCursor(page, LIMIT_DEFAULT)
  const queryOpenAccess =
    params.hasOpenAccess !== undefined ? `hasOpenAccess: ${hasOpenAccess}, ` : ''

  return gql`
    {
      accessReviewObjects(
        first: ${pageSize},
        after: "${cursor}",
        reviewId: "${reviewId}"
        ${queryOpenAccess}
      ) {
        edges {
          node {
            name
            size
            fileId
            objectLink
            extension
            documentId
            createdTimestamp
            lastModifiedTimestamp
          }
        }
      }
    }
  `
}
export const mapQueryAccessReviewObjectsList = (raw: any): AccessReviewObject[] => {
  try {
    const list: AccessReviewObject[] =
      raw.accessReviewObjects.edges.map(({ node }) => ({
        name: node?.name,
        size: node?.size,
        fileId: node?.fileId,
        objectLink: node?.objectLink,
        extension: node?.extension,
        documentId: node?.documentId,
        createdTimestamp: node?.createdTimestamp,
        lastModifiedTimestamp: node?.lastModifiedTimestamp
      })) || []

    return list
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryAccessReviewObjectsCount = (params: AccessReviewObjectsParams): string => {
  const { reviewId, hasOpenAccess } = params

  return gql`
    {
      accessReviewObjects(
      reviewId: "${reviewId}"
      ${hasOpenAccess !== undefined ? `hasOpenAccess: ${hasOpenAccess}` : ''}
      first: 1
      ) {
        count
      }
    }
  `
}
export const mapQueryAccessReviewObjectsCount = (raw: any): number => {
  try {
    return raw.accessReviewObjects.count || 0
  } catch (error) {
    console.error(error)
    throw error
  }
}
